/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDateFormatter, useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { useRequestMe, useSession } from "@packages/sdk";
import { differenceInCalendarYears } from "date-fns/differenceInCalendarYears";
import { parse } from "date-fns/parse";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { ThemeType } from "../../../../global/emotion";
import { radius, spacing, useTheme } from "../../../../global/emotion";
import { useValidator } from "../../../hooks/useValidator.emotion";
import {
  cssMediaQueryIsSafari,
  cssMediaQueryMinWidthTablet,
} from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert, useConfirm } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps, AuthProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
};
const headerStyle: Interpolation = {
  height: spacing.xl,
  width: "100%",
};
const inputFormFieldStyle: Interpolation<ThemeType> = (theme) => ({
  minHeight: spacing.l,
  width: spacing.full,
  [cssMediaQueryIsSafari]: {
    background: theme.semanticColors.neutralsVeryLow,
    borderRadius: radius.s,
  },
  [cssMediaQueryMinWidthTablet]: {
    [cssMediaQueryIsSafari]: {
      background: "unset",
      borderRadius: "unset",
    },
  },
});

export const AuthDOBFlow = ({
  hideHeader,
  onAuth,
  className,
  setFlow,
  setState,
}: Omit<AuthFlowProps, "onAuth"> & Pick<AuthProps, "onAuth">) => {
  const { query: meQuery } = useRequestMe();
  const { logout } = useSession();
  const t = useTranslations();
  const format = useDateFormatter();
  const { renderedTheme } = useTheme();
  const confirmUnderage = useConfirm<{ dob: Date }>({
    cancelButton: (onClick) => (
      <Button
        variant={renderedTheme === "dark" ? "darken" : "neutral"}
        onClick={onClick}
        isFullWidth
      >
        {t("dob_no_cancel")}
      </Button>
    ),
    description: ({ dob }) =>
      t.rich("dob_confirm_date_alert_message", {
        0: format(dob, "MMM dd, yyyy"),
        b: (chunks) => <strong>{chunks}</strong>,
      }),
    primaryButton: (onClick) => (
      <Button
        variant={renderedTheme === "dark" ? "darken" : "neutral"}
        onClick={onClick}
        isFullWidth
      >
        {t("dob_yes_correct")}
      </Button>
    ),
    title: () => t("dob_confirm_date_alert_title"),
    buttonFormat: "vertical-reverse",
  });
  const errorUnderage = useAlert({
    title: () => t("dob_ineligible_alert_title"),
    description: () => t("dob_ineligible_alert_message"),
  });

  const authFlowDobDataSchema = z.object({
    dob: z.iso.date("dob_input_error"),
  });
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } = useValidator<
    z.infer<typeof authFlowDobDataSchema>
  >(zodResolver(authFlowDobDataSchema));
  const form = useForm({ resolver });
  const analytics = useAnalytics();

  const { mutationPut } = useRequestMe();
  useViewedScreen({ screen: "birthdate" });

  const onSubmit = async ({ dob }: z.infer<typeof authFlowDobDataSchema>) => {
    try {
      await mutationPut.mutateAsync({ birthdate: dob });
    } catch (error) {}
    const userEnteredDob = parse(dob, "yyyy-MM-dd", new Date());
    if (Math.abs(differenceInCalendarYears(userEnteredDob, new Date())) < 13) {
      const confirmed = await confirmUnderage.show({ dob: userEnteredDob });
      analytics.track({
        event: HallowAnalyticsEvent.TappedConfirmDOB,
        properties: {
          option: confirmed ? "yes_correct" : "no_cancel",
        },
      });
      if (confirmed) {
        await errorUnderage.show();
        logout({
          callback: () => {
            setState({});
            setFlow("main");
          },
        });
      }
      return;
    }
    onAuth({ isNewUser: true });
  };

  return (
    <AuthForm<z.infer<typeof authFlowDobDataSchema>>
      form={form}
      defaultValues={{
        dob: meQuery.data?.birthdate || "",
      }}
      onSubmit={onSubmit}
      footer={(_, disabled) => (
        <div css={buttonsStyle}>
          <AuthSubmitButton disabled={disabled}>
            {t("onboarding_button_continue")}
          </AuthSubmitButton>
        </div>
      )}
      className={className}
    >
      {(form) => (
        <>
          {!hideHeader && <div css={headerStyle} />}
          <Text type="headline" size="m" as="h2">
            {t("dob_what_is_your_birthday")}
          </Text>
          <FormField
            control={form.control}
            name="dob"
            showErrorMessage={false}
            defaultValue={""}
            render={({ field }) => (
              <AuthTextInput
                required
                autoFocus
                autoComplete="bday"
                autoCorrect="off"
                css={fieldsCSS[field.name]}
                type="date"
                {...inputProps}
                {...field}
              />
            )}
            css={inputFormFieldStyle}
          />
          <AuthError css={errorsCSS.dob}>
            {errors?.dob?.message ? t(errors.dob.message) : " "}
          </AuthError>
        </>
      )}
    </AuthForm>
  );
};
